import { LitElement, css, html } from 'lit';
import { shared } from './shared.ts';

type SkinViewer = import('skinview3d').SkinViewer;

/**
 * Lit adapter around skinview3d. WebGL code loads only when the element nears the viewport,
 * animation pauses offscreen and under reduced motion, and everything is disposed on disconnect.
 * Skins come from our own release assets — never fetched by real account name.
 */
export class KaSkinViewer extends LitElement {
  static override properties = {
    skin: {},
    still: {},
    model: {},
    label: {},
    failed: { state: true },
  };
  declare skin: string;
  declare still: string;
  declare model: 'classic' | 'slim';
  declare label: string;
  declare failed: boolean;

  private viewer?: SkinViewer;
  private io?: IntersectionObserver;
  private motion = matchMedia('(prefers-reduced-motion: reduce)');

  static override styles = [
    shared,
    css`
      .stage {
        aspect-ratio: 3 / 4;
        max-width: 320px;
        background: radial-gradient(var(--ka-stone-700), var(--ka-stone-900));
        border: 2px solid #000;
        box-shadow: var(--ka-bevel);
        display: grid;
        place-items: center;
      }
      canvas {
        width: 100%;
        height: 100%;
        touch-action: pan-y;
      }
      img {
        max-width: 100%;
        image-rendering: pixelated;
      }
    `,
  ];

  constructor() {
    super();
    this.skin = '';
    this.still = '';
    this.model = 'classic';
    this.label = 'Character model';
    this.failed = false;
  }

  override firstUpdated() {
    if (!this.skin) return;
    this.io = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) void this.start();
      if (this.viewer) this.viewer.renderPaused = !entry?.isIntersecting;
    });
    this.io.observe(this);
  }

  private async start() {
    if (this.viewer || this.failed) return;
    const canvas = this.renderRoot.querySelector('canvas');
    if (!canvas) return;
    try {
      const { SkinViewer, IdleAnimation } = await import('skinview3d');
      if (!this.isConnected) return;
      this.viewer = new SkinViewer({
        canvas,
        width: 300,
        height: 400,
        skin: this.skin,
        model: this.model === 'slim' ? 'slim' : 'default',
      });
      this.viewer.controls.enableZoom = false;
      if (!this.motion.matches) this.viewer.animation = new IdleAnimation();
    } catch {
      this.failed = true;
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.io?.disconnect();
    this.viewer?.dispose();
    this.viewer = undefined;
  }

  override render() {
    if (!this.skin || this.failed) {
      return html`<div class="stage" role="img" aria-label=${this.label}>
        ${this.still ? html`<img src=${this.still} alt=${this.label} />` : html`<slot></slot>`}
      </div>`;
    }
    return html`<div class="stage">
      <canvas role="img" aria-label="${this.label}. Drag to rotate."></canvas>
    </div>`;
  }
}
customElements.define('ka-skin-viewer', KaSkinViewer);
